import React, { useEffect, useState } from "react";
import {
  get as getInbox,
  getStats,
  InboxStats,
  bootstrap,
  StatsPeriod,
} from "../lib/inboxLib";
import { get as getMessage, getPageForInbox } from "../lib/messagesLib";
import MessageRow from "./messageRow";
import { fetchAllMail, refreshPubSub } from "../lib/utilitiesLib";
import JsonModal from "./JsonModal";
import { timeAgo } from "../lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  subscribeToEvent,
  unsubscribeFromEvent,
  EventNames,
} from "../lib/socketService";

interface InboxCardProps {
  inboxId: string;
}

const InboxCard: React.FC<InboxCardProps> = ({ inboxId }) => {
  const [inbox, setInbox] = useState<any | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState<InboxStats | null>(null);
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    title: string;
    data: any;
  }>({
    isOpen: false,
    title: "",
    data: null,
  });

  useEffect(() => {
    const fetchInboxAndMessages = async () => {
      // Fetch inbox
      try {
        const inboxData = await getInbox(inboxId);
        setInbox(inboxData);
      } catch (error) {
        console.error("Error fetching inbox:", error);
      } finally {
        setInboxLoading(false);
      }

      // Fetch messages
      try {
        const messagesData = await getPageForInbox(inboxId);
        setMessages(messagesData.messages);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setMessagesLoading(false);
      }

      // Fetch stats
      try {
        const statsData = await getStats(inboxId);
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchInboxAndMessages();
  }, [inboxId]);

  useEffect(() => {
    // Subscribe to socket events for this specific inbox
    const handleMessageReceived = (data: any) => {
      if (data.message.inboxId === inboxId) {
        setMessages((prevMessages) => [data.message, ...prevMessages]);
      }
    };

    const handleMessageUpdated = (data: any) => {
      if (data.message.inboxId === inboxId) {
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === data.message.id ? data.message : msg
          )
        );
      }
    };

    const handleInboxUpdated = (data: any) => {
      if (data.inbox.id === inboxId) {
        setInbox(data.inbox);
      }
    };

    const handleHealthStatusUpdated = (data: any) => {
      if (data.inboxId === inboxId) {
        setStats((prevStats) => {
          if (!prevStats) {
            return {
              period: StatsPeriod.ONE_WEEK,
              startDate: "",
              endDate: "",
              messagesReceived: 0,
              messagesFiltered: 0,
              messagesAllowed: 0,
              challengesCreated: 0,
              challengesViewed: 0,
              donationsReceived: { cents: 0, count: 0 },
              statsHistory: [],
              statsHistoryGroupingInterval: "day",
              health: data.health,
            };
          }
          return {
            ...prevStats,
            health: data.health,
          };
        });
      }
    };

    // Subscribe to events
    subscribeToEvent(EventNames.MESSAGE_RECEIVED, handleMessageReceived);
    subscribeToEvent(EventNames.MESSAGE_UPDATED, handleMessageUpdated);
    subscribeToEvent(EventNames.INBOX_UPDATED, handleInboxUpdated);
    subscribeToEvent(
      EventNames.HEALTH_STATUS_UPDATED,
      handleHealthStatusUpdated
    );

    // Cleanup function
    return () => {
      unsubscribeFromEvent(EventNames.MESSAGE_RECEIVED, handleMessageReceived);
      unsubscribeFromEvent(EventNames.MESSAGE_UPDATED, handleMessageUpdated);
      unsubscribeFromEvent(EventNames.INBOX_UPDATED, handleInboxUpdated);
      unsubscribeFromEvent(
        EventNames.HEALTH_STATUS_UPDATED,
        handleHealthStatusUpdated
      );
    };
  }, [inboxId]);

  const handleRefreshPubSub = async () => {
    try {
      const response = await refreshPubSub(inboxId);
      setModalData({
        isOpen: true,
        title: "Pub/Sub Refresh Results",
        data: response,
      });
    } catch (error) {
      console.error("Error refreshing Pub/Sub:", error);
      setModalData({
        isOpen: true,
        title: "Error",
        data: { error: "Failed to refresh Pub/Sub" },
      });
    }
  };

  const handleFetchAllMail = async () => {
    try {
      const response = await fetchAllMail(inboxId);
      setModalData({
        isOpen: true,
        title: "Mail Refetch Results",
        data: response,
      });
    } catch (error) {
      console.error("Error refetching all mail:", error);
      setModalData({
        isOpen: true,
        title: "Error",
        data: { error: "Failed to refetch all mail" },
      });
    }
  };
  const bootstrapInboxHandler = async () => {
    try {
      const response = await bootstrap(inboxId);
      setModalData({
        isOpen: true,
        title: "Bootstrap Results",
        data: response,
      });
    } catch (error) {
      console.error("Error bootstrapping inbox:", error);
      setModalData({
        isOpen: true,
        title: "Error",
        data: { error: "Failed to bootstrap inbox" },
      });
    }
  };

  const refreshMessage = async (messageId: string) => {
    try {
      const updatedMessage = await getMessage(messageId);
      setMessages(
        messages.map((msg) => (msg.id === messageId ? updatedMessage : msg))
      );
    } catch (error) {
      console.error("Error refreshing message:", error);
    }
  };

  if (inboxLoading) {
    return <div>Loading inbox...</div>;
  }

  if (!inbox) {
    return <div>Inbox not found</div>;
  }

  return (
    <div className="inbox-card text-gray-700 dark:text-gray-300">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <h2 className="text-xl font-semibold mb-2">Inbox: {inbox.name}</h2>
          <p className="text-sm mb-2">
            Latest historyId: {inbox.latestSyncedGmailHistoryId}, last sync was{" "}
            {timeAgo(inbox.lastSyncedAt)}{" "}
            {inbox.lastSyncedAt ? "at " + inbox.lastSyncedAt : ""}
          </p>
          <div className="flex space-x-2 mb-4">
            <button
              onClick={handleRefreshPubSub}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
            >
              Remake pub/sub
            </button>
            <button
              onClick={handleFetchAllMail}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
            >
              Refetch messages
            </button>
            <button
              onClick={bootstrapInboxHandler}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
            >
              Bootstrap inbox
            </button>
          </div>

          {messagesLoading ? (
            <div className="text-center py-4">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No messages stored for this inbox
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Timestamp</th>
                    <th>Addresses</th>
                    <th>Subject + Labels</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="text-xs bg-white divide-y divide-gray-200 dark:divide-gray-900 dark:bg-gray-800">
                  {messages.map((message) => (
                    <MessageRow
                      key={message.id}
                      message={message}
                      labelMap={inbox.labels}
                      onMessageUpdate={() => refreshMessage(message.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-gray-100 dark:bg-gray-700 rounded p-4">
          <h3 className="text-lg font-semibold mb-2">7 Day Stats</h3>
          {statsLoading ? (
            <div className="text-sm">Loading stats...</div>
          ) : stats ? (
            <>
              <div className="text-sm space-y-1 mb-4">
                <p>Messages: {stats.messagesReceived}</p>
                <p>Filtered: {stats.messagesFiltered}</p>
                <p>Challenges Created: {stats.challengesCreated}</p>
                <p>Challenges Viewed: {stats.challengesViewed}</p>
                <p>
                  Donations: {stats.donationsReceived.count} ($
                  {(stats.donationsReceived.cents / 100).toFixed(2)})
                </p>
              </div>

              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.statsHistory}>
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) =>
                        new Date(date).toLocaleDateString(undefined, {
                          month: "numeric",
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(date) =>
                        new Date(date).toLocaleDateString()
                      }
                      formatter={(value, name) => [
                        value,
                        String(name)
                          .replace(/([A-Z])/g, " $1")
                          .trim(),
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="messagesReceived"
                      stroke="#4F46E5"
                      name="Total"
                    />
                    <Line
                      type="monotone"
                      dataKey="messagesFiltered"
                      stroke="#EF4444"
                      name="Filtered"
                    />
                    <Line
                      type="monotone"
                      dataKey="challengesCreated"
                      stroke="#10B981"
                      name="Challenges Created"
                    />
                    <Line
                      type="monotone"
                      dataKey="challengesViewed"
                      stroke="#F59E0B"
                      name="Challenges Viewed"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="text-sm text-red-500">Failed to load stats</div>
          )}
        </div>
      </div>

      <JsonModal
        isOpen={modalData.isOpen}
        onClose={() => setModalData({ isOpen: false, title: "", data: null })}
        title={modalData.title}
        data={modalData.data}
      />
    </div>
  );
};

export default InboxCard;
